import { observer, useLocalObservable } from "mobx-react-lite";
import React, { useEffect } from "react";

import UnifiedRegistryStore from "./unified-registry-store";

import { Entity, EntityType } from "@/queries";

const UnifiedRegistryContext = React.createContext<UnifiedRegistryStore | null>(null);

export const useUnifiedRegistry = () => {
  const context = React.useContext(UnifiedRegistryContext);
  if (!context) {
    throw new Error("useUnifiedRegistry must be used within UnifiedRegistryProvider");
  }
  return context;
};

export interface UnifiedRegistryOptions {
  whitelist?: EntityType[];
  disableSelection?: boolean;
  disabled?: Entity[] | ((entity: Entity) => boolean);
  createInNewTab?: boolean;
  modal?: boolean;
  multiSelectMode?: boolean;
  allowDuplicateVariants?: boolean;
}

interface UnifiedRegistryProviderProps {
  children: React.ReactNode;
  options?: UnifiedRegistryOptions;
}

export const UnifiedRegistryProvider = observer(
  ({ children, options }: UnifiedRegistryProviderProps) => {
    const store = useLocalObservable(() => new UnifiedRegistryStore(options ?? {}));

    useEffect(() => {
      store.setOptions(options ?? {});
    }, [store, options]);

    return (
      <UnifiedRegistryContext.Provider value={store}>{children}</UnifiedRegistryContext.Provider>
    );
  },
);

export const withUnifiedRegistryStore = <P extends object>(
  Component: React.ComponentType<P>,
  options?: UnifiedRegistryOptions,
) =>
  React.forwardRef<unknown, P>((props, ref) => (
    <UnifiedRegistryProvider options={options}>
      <Component {...(props as P)} ref={ref} />
    </UnifiedRegistryProvider>
  ));
